import React, { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Avatar, genderColor } from '../lib/avatars';
import { NEARME_EMOJIS, EmojiSVG, renderMessage } from '../lib/nearmeEmojis';

const intentionColors = {'Relationship':'#00d68f','Something casual':'#ff9500','Friendship':'#0084ff','Not sure yet':'#888'};

export default function Chat({ user, session, onClose, onBlock }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEmojis, setShowEmojis] = useState(false);
  const [view, setView] = useState('chat'); // chat | block | report | profile
  const [reportReason, setReportReason] = useState('');
  const [reportMsg, setReportMsg] = useState('');
  const [blockDone, setBlockDone] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const myId = session?.user?.id;
  const color = genderColor(user?.gender);

  const markRead = useCallback(async (ids) => {
    if (!ids.length) return;
    await supabase.from('messages').update({ read: true }).in('id', ids);
  }, []);

  useEffect(() => {
    if (!myId || !user?.id) {
      setMessages([{ id:1, sender_id:user.id, content:'Hey!', created_at:new Date().toISOString(), read:true }]);
      setLoading(false); return;
    }
    const load = async () => {
      const { data } = await supabase.from('messages').select('*')
        .or('sender_id.eq.' + myId + ',receiver_id.eq.' + myId)
        .order('created_at', { ascending: true });
      const msgs = (data || []).filter(m =>
        (m.sender_id === myId && m.receiver_id === user.id) ||
        (m.sender_id === user.id && m.receiver_id === myId)
      );
      setMessages(msgs);
      setLoading(false);
      markRead(msgs.filter(m => m.sender_id === user.id && !m.read).map(m => m.id));
    };
    load();
    const ch = supabase.channel('chat_' + [myId, user.id].sort().join('_'))
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'messages' }, p => {
        const msg = p.new;
        if ((msg.sender_id === user.id && msg.receiver_id === myId) || (msg.sender_id === myId && msg.receiver_id === user.id)) {
          setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg]);
          if (msg.sender_id === user.id) markRead([msg.id]);
        }
      })
      .on('postgres_changes', { event:'UPDATE', schema:'public', table:'messages' }, p => {
        setMessages(prev => prev.map(m => m.id === p.new.id ? { ...m, read: p.new.read } : m));
      }).subscribe();
    return () => supabase.removeChannel(ch);
  }, [myId, user?.id, markRead]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    const msg = text;
    setText(''); setShowEmojis(false);
    if (!myId || !user?.id) return;
    await supabase.from('messages').insert({ sender_id:myId, receiver_id:user.id, content:msg, read:false });
  };

  const doBlock = async () => {
    if (!myId || !user?.id) return;
    await supabase.from('blocks').delete().eq('blocker_id', myId).eq('blocked_id', user.id);
    const { error } = await supabase.from('blocks').insert({ blocker_id:myId, blocked_id:user.id });
    if (error) { alert('Error: ' + error.message); return; }
    setBlockDone(true);
    setTimeout(() => { if (onBlock) onBlock(user.id); onClose(); }, 1400);
  };

  const doReport = async () => {
    if (!reportReason || !myId || !user?.id) return;
    const { error } = await supabase.from('reports').insert({ reporter_id:myId, reported_id:user.id, reason:reportReason, message:reportMsg || null });
    if (error) { alert('Error: ' + error.message); return; }
    setReportDone(true);
    setTimeout(() => { setView('chat'); setReportDone(false); setReportReason(''); setReportMsg(''); }, 1800);
  };

  const isMe = m => m.sender_id === myId || m.sender_id === 'me';
  const fmt = ts => new Date(ts).toLocaleTimeString('en', { hour:'2-digit', minute:'2-digit' });
  const sentMsgs = messages.filter(m => isMe(m));
  const lastSentId = sentMsgs[sentMsgs.length - 1]?.id;

  // =================== PROFILE VIEW ===================
  if (view === 'profile') return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:300, display:'flex', flexDirection:'column', overflowY:'auto' }}>
      <div style={{ padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
        <button onClick={() => setView('chat')} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', cursor:'pointer', fontSize:'0.88rem', padding:0 }}>← Back</button>
        <div style={{ fontSize:'0.88rem', fontWeight:600, color:'white' }}>Profile</div>
        <div style={{ width:'60px' }}/>
      </div>
      <div style={{ padding:'32px 24px', display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center' }}>
        <div style={{ width:'90px', height:'90px', borderRadius:'50%', background:'rgba(8,8,8,0.9)', border:'3px solid ' + color, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'16px' }}>
          <Avatar gender={user.gender} userId={user.id} size={56}/>
        </div>
        <div style={{ fontWeight:700, color:'white', fontSize:'1.3rem', letterSpacing:'-0.5px', marginBottom:'4px' }}>
          {user.name || 'User'}{user.age ? ', ' + user.age : ''}
        </div>
        <div style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.82rem', marginBottom:'8px' }}>{user.gender}</div>
        {user.online && (
          <div style={{ display:'flex', alignItems:'center', gap:'5px', marginBottom:'10px' }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background:'#00d68f' }}/>
            <span style={{ color:'#00d68f', fontSize:'0.78rem' }}>Online now</span>
          </div>
        )}
        {user.intention && (
          <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'100px', padding:'5px 16px', fontSize:'0.78rem', color: intentionColors[user.intention] || '#888', marginBottom:'14px' }}>
            {user.intention}
          </div>
        )}
        {user.bio && <p style={{ color:'rgba(255,255,255,0.45)', fontSize:'0.88rem', lineHeight:1.7, maxWidth:'300px', marginBottom:'14px' }}>{user.bio}</p>}
        {user.dist && <div style={{ color:'rgba(255,255,255,0.18)', fontSize:'0.72rem', marginBottom:'24px' }}>~{user.dist} km away</div>}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', width:'100%', maxWidth:'300px' }}>
          <button onClick={() => setView('report')} style={{ padding:'12px', background:'rgba(255,165,0,0.08)', border:'1px solid rgba(255,165,0,0.2)', borderRadius:'13px', color:'rgba(255,165,0,0.85)', fontSize:'0.85rem', cursor:'pointer' }}>Report</button>
          <button onClick={() => setView('block')} style={{ padding:'12px', background:'rgba(255,50,50,0.08)', border:'1px solid rgba(255,50,50,0.2)', borderRadius:'13px', color:'rgba(255,80,80,0.85)', fontSize:'0.85rem', cursor:'pointer' }}>Block</button>
        </div>
      </div>
    </div>
  );

  // =================== BLOCK VIEW ===================
  if (view === 'block') return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:300, padding:'0 0 24px' }}>
      <div style={{ background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'24px', padding:'28px 24px', width:'calc(100% - 32px)', maxWidth:'400px' }}>
        {!blockDone ? (
          <>
            <div style={{ fontWeight:700, color:'white', fontSize:'1rem', marginBottom:'8px' }}>Block {user?.name}?</div>
            <div style={{ background:'rgba(255,50,50,0.05)', border:'1px solid rgba(255,50,50,0.1)', borderRadius:'12px', padding:'12px', marginBottom:'16px' }}>
              {['Removed from your map','Cannot message you','You disappear from their map','Reversible from your Profile'].map((item, i) => (
                <div key={i} style={{ display:'flex', gap:'8px', marginBottom: i < 3 ? '7px' : 0 }}>
                  <div style={{ width:4, height:4, borderRadius:'50%', background:'rgba(255,100,100,0.6)', flexShrink:0, marginTop:6 }}/>
                  <span style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.8rem' }}>{item}</span>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={() => setView('chat')} style={{ flex:1, padding:'12px', background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'12px', color:'rgba(255,255,255,0.5)', cursor:'pointer' }}>Cancel</button>
              <button onClick={doBlock} style={{ flex:1, padding:'12px', background:'#cc2222', border:'none', borderRadius:'12px', color:'white', fontWeight:700, cursor:'pointer' }}>Block</button>
            </div>
          </>
        ) : (
          <div style={{ textAlign:'center', padding:'10px 0' }}>
            <div style={{ color:'white', fontWeight:700, marginBottom:'4px' }}>Blocked ✓</div>
            <div style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.82rem' }}>{user?.name} can no longer interact with you.</div>
          </div>
        )}
      </div>
    </div>
  );

  // =================== REPORT VIEW ===================
  if (view === 'report') return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:300, padding:'0 0 24px' }}>
      <div style={{ background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'24px', padding:'24px', width:'calc(100% - 32px)', maxWidth:'400px' }}>
        {!reportDone ? (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <div style={{ fontWeight:700, color:'white', fontSize:'1rem' }}>Report {user?.name}</div>
              <button onClick={() => setView('chat')} style={{ background:'rgba(255,255,255,0.07)', border:'none', borderRadius:'50%', width:'30px', height:'30px', cursor:'pointer', color:'white', fontSize:'18px', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'7px', marginBottom:'12px' }}>
              {['Fake profile','Harassment','Spam','Inappropriate','Underage','Other'].map(r => (
                <button key={r} onClick={() => setReportReason(r)} style={{ padding:'10px', background: reportReason === r ? 'rgba(255,165,0,0.12)' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (reportReason === r ? 'rgba(255,165,0,0.4)' : 'rgba(255,255,255,0.07)'), borderRadius:'11px', color: reportReason === r ? 'rgba(255,165,0,0.9)' : 'rgba(255,255,255,0.5)', fontSize:'0.82rem', cursor:'pointer', fontWeight: reportReason === r ? 600 : 400 }}>{r}</button>
              ))}
            </div>
            <textarea value={reportMsg} onChange={e => setReportMsg(e.target.value)} placeholder="Additional details (optional)..." rows={2}
              style={{ width:'100%', padding:'10px 12px', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'11px', color:'white', fontSize:'0.82rem', resize:'none', outline:'none', marginBottom:'12px', boxSizing:'border-box' }}/>
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={() => setView('chat')} style={{ flex:1, padding:'12px', background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'12px', color:'rgba(255,255,255,0.5)', cursor:'pointer' }}>Cancel</button>
              <button onClick={doReport} disabled={!reportReason} style={{ flex:1, padding:'12px', background: reportReason ? 'rgba(255,165,0,0.85)' : 'rgba(255,255,255,0.08)', border:'none', borderRadius:'12px', color: reportReason ? 'white' : 'rgba(255,255,255,0.2)', fontWeight:600, cursor: reportReason ? 'pointer' : 'not-allowed' }}>Submit</button>
            </div>
          </>
        ) : (
          <div style={{ textAlign:'center', padding:'10px 0' }}>
            <div style={{ color:'white', fontWeight:700, marginBottom:'4px' }}>Report submitted ✓</div>
            <div style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.82rem' }}>Our team reviews within 24h.</div>
          </div>
        )}
      </div>
    </div>
  );

  // =================== CHAT VIEW ===================
  return (
    <div style={{ position:'fixed', top:'70px', right:'16px', width:'min(340px,calc(100vw - 32px))', background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'20px', overflow:'hidden', boxShadow:'0 24px 60px rgba(0,0,0,0.9)', zIndex:200, display:'flex', flexDirection:'column', maxHeight:'calc(100vh - 90px)' }}>

      {/* Header */}
      <div style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', gap:'10px', background:'rgba(255,255,255,0.02)', flexShrink:0 }}>
        <div style={{ flexShrink:0, cursor:'pointer' }} onClick={() => setView('profile')}>
          <Avatar gender={user.gender} userId={user.id} size={36}/>
        </div>
        <div style={{ flex:1, minWidth:0, cursor:'pointer' }} onClick={() => setView('profile')}>
          <div style={{ fontWeight:600, fontSize:'0.87rem', color:'white', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.name || 'User'}{user.age ? `, ${user.age}` : ''}</div>
          <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
            <div style={{ width:5, height:5, borderRadius:'50%', background: user.online ? '#00d68f' : 'rgba(255,255,255,0.2)' }}/>
            <span style={{ color: user.online ? '#00d68f' : 'rgba(255,255,255,0.3)', fontSize:'0.65rem' }}>{user.online ? 'Online' : 'Offline'}</span>
          </div>
        </div>
        <button onClick={() => setView('report')} title="Report" style={{ width:'30px', height:'30px', background:'rgba(255,165,0,0.07)', border:'1px solid rgba(255,165,0,0.2)', borderRadius:'8px', cursor:'pointer', color:'rgba(255,165,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.3"/><path d="M6 3.5V6.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><circle cx="6" cy="8.5" r="0.7" fill="currentColor"/></svg>
        </button>
        <button onClick={() => setView('block')} title="Block" style={{ width:'30px', height:'30px', background:'rgba(255,50,50,0.07)', border:'1px solid rgba(255,50,50,0.2)', borderRadius:'8px', cursor:'pointer', color:'rgba(255,80,80,0.8)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.3"/><line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
        </button>
        <button onClick={onClose} style={{ background:'rgba(255,255,255,0.05)', border:'none', borderRadius:'8px', width:'30px', height:'30px', cursor:'pointer', color:'rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'16px' }}>×</button>
      </div>

      {/* Messages */}
      <div style={{ flex:1, overflowY:'auto', padding:'12px', display:'flex', flexDirection:'column', gap:'3px', minHeight:'200px' }}>
        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100%' }}>
            <div style={{ width:'18px', height:'18px', border:'2px solid rgba(255,255,255,0.08)', borderTop:'2px solid white', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
          </div>
        ) : messages.map(m => (
          <div key={m.id} style={{ display:'flex', flexDirection:'column', alignItems: isMe(m) ? 'flex-end' : 'flex-start' }}>
            <div style={{ background: isMe(m) ? color : 'rgba(255,255,255,0.08)', color: isMe(m) ? '#080808' : 'white', padding:'8px 12px', borderRadius: isMe(m) ? '14px 14px 3px 14px' : '14px 14px 14px 3px', maxWidth:'82%', fontSize:'0.84rem', lineHeight:1.5, wordBreak:'break-word' }}>
              {m.photo_url ? <img src={m.photo_url} alt="" style={{ width:'100%', maxWidth:'200px', borderRadius:'8px', display:'block' }}/> : renderMessage(m.content)}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'4px', marginTop:'2px' }}>
              <span style={{ color:'rgba(255,255,255,0.18)', fontSize:'0.62rem' }}>{fmt(m.created_at)}</span>
              {isMe(m) && m.id === lastSentId && (
                m.read
                  ? <svg width="14" height="9" viewBox="0 0 14 9" fill="none"><path d="M1 4.5L3.5 7L8 1.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 7L10.5 1.5M13 1.5L8.5 7" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  : <svg width="10" height="9" viewBox="0 0 10 9" fill="none"><path d="M1 4.5L3.5 7L8 1.5" stroke="rgba(255,255,255,0.25)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef}/>
      </div>

      {/* Emojis */}
      {showEmojis && (
        <div style={{ padding:'8px 12px', display:'flex', flexWrap:'wrap', gap:'5px', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
          {NEARME_EMOJIS.map(emoji => (
            <button key={emoji.id} onClick={() => { setText(prev => prev + '[nearme:' + emoji.id + ']'); setShowEmojis(false); inputRef.current?.focus(); }}
              style={{ width:'34px', height:'34px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'8px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <EmojiSVG id={emoji.id} size={18}/>
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ padding:'10px 12px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', gap:'7px', alignItems:'center', flexShrink:0 }}>
        <button onClick={() => setShowEmojis(!showEmojis)} style={{ width:'32px', height:'32px', background: showEmojis ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'50%', cursor:'pointer', color: showEmojis ? 'white' : 'rgba(255,255,255,0.4)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3"/><circle cx="5.5" cy="6.5" r="1" fill="currentColor"/><circle cx="10.5" cy="6.5" r="1" fill="currentColor"/><path d="M5 10C5 10 6 12 8 12C10 12 11 10 11 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
        </button>
        <input ref={inputRef} value={text} onChange={e => setText(e.target.value)} onKeyPress={e => e.key === 'Enter' && send()} placeholder={'Message ' + (user.name || '') + '...'}
          style={{ flex:1, padding:'9px 12px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'100px', color:'white', fontSize:'0.84rem', outline:'none' }}/>
        <button onClick={send} style={{ width:'36px', height:'36px', background:color, border:'none', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12 7L2 2L4 7L2 12L12 7Z" fill="white"/></svg>
        </button>
      </div>
    </div>
  );
}
